/**
 * The verifier, the S256 challenge and the state of one kernel handoff (kernel apps, WS-16a,
 * design section 3.3, RFC 7636).
 */

/** The random bytes of a verifier: 32, so 43 base64url characters (RFC 7636 section 4.1). */
const VERIFIER_BYTES = 32;

/** The random bytes of a state: 16, so 22 base64url characters. */
const STATE_BYTES = 16;

/** The base64url alphabet of RFC 4648 section 5. */
const BASE64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/**
 * One handoff's secrets.
 *
 * - `verifier`: 43 base64url characters; it passes `PKCE_CODE_VERIFIER_PATTERN` of the WS-1b
 *   handoff contract.
 * - `challenge`: the 43-character base64url SHA-256 of the verifier; it passes
 *   `PKCE_CODE_CHALLENGE_PATTERN`.
 * - `state`: 22 base64url characters; it passes `KERNEL_HANDOFF_STATE_PATTERN`.
 */
export interface PkcePair {
  readonly verifier: string;
  readonly challenge: string;
  readonly state: string;
}

/**
 * The base64url text of `bytes`, with the alphabet `A-Z a-z 0-9 - _` and no padding. An empty
 * array gives `''`. Pure.
 */
export function base64UrlOf(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i] as number;
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    const n = (a << 16) | ((b ?? 0) << 8) | (c ?? 0);
    out += BASE64URL_ALPHABET.charAt((n >> 18) & 63) + BASE64URL_ALPHABET.charAt((n >> 12) & 63);
    if (b !== undefined) out += BASE64URL_ALPHABET.charAt((n >> 6) & 63);
    if (c !== undefined) out += BASE64URL_ALPHABET.charAt(n & 63);
  }
  return out;
}

/**
 * Make one handoff's secrets with `crypto`.
 *
 * `beginKernelSignIn` calls it once per handoff. It draws 32 random bytes for the verifier, then 16
 * for the state, with `crypto.getRandomValues`. The challenge is `base64UrlOf(SHA-256(verifier))`
 * through `crypto.subtle.digest`, over the ASCII bytes of the verifier text.
 *
 * Errors: an absent `crypto.subtle` rejects with `TypeError`. A throw from `getRandomValues` and a
 * rejected `digest` pass their reason on. `beginKernelSignIn` maps each rejection to `no-crypto`.
 * Pure apart from the random draw.
 */
export async function createPkcePair(crypto: Crypto): Promise<PkcePair> {
  const verifier = base64UrlOf(crypto.getRandomValues(new Uint8Array(VERIFIER_BYTES)));
  const state = base64UrlOf(crypto.getRandomValues(new Uint8Array(STATE_BYTES)));
  const subtle: SubtleCrypto | undefined = crypto.subtle;
  if (subtle === undefined) throw new TypeError('crypto.subtle is unavailable');
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: base64UrlOf(new Uint8Array(digest)), state };
}
